import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  linkedSignal,
  OnDestroy,
  OnInit,
  output,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { determinePublicationStatus } from '@wilson/activities/pipes';
import {
  AuditTrailDrawerState,
  SetAuditTrailDrawerCurrentShiftIdState,
  SetAuditTrailDrawerOpenState,
} from '@wilson/audit-trail/states';
import { AuthState } from '@wilson/auth/core';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  FeatureName,
  OPEN_FIND_STAFF,
  OperationStatus,
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  Shift,
  ShiftDrawerCollapseSectionEnum,
  ShiftState,
} from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { DrawerGroupingActionsService } from '@wilson/non-domain-specific/groupings';
import {
  CustomNzDrawerService,
  NzDrawerId,
} from '@wilson/non-domain-specific/ng-zorro/nz-drawer/core';
import { TimelinePresenceService } from '@wilson/non-domain-specific/realtime-collaboration/live-viewers';
import {
  ShiftQuickCheckService,
  TimelineShiftDrawerFormValue,
} from '@wilson/non-domain-specific/shift-helpers/core';
import {
  SetShiftDrawerCollapseSectionSettings,
  ShiftDrawerSettingsState,
} from '@wilson/preferred-settings/state';
import {
  ShiftDrawerCloseHelperService,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  SetDemarcatorRange,
  ShiftTimelineSettingsState,
  TimelineAssignedShiftValidationsStateService,
  TimelineRemoveActivityFromShiftV2,
  TimelineStaysSuggestionService,
  TimelineUnassignedShiftValidationsStateService,
  TimelineUpsertActivityToUnassignedRegion,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { ShiftSeriesService, ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  fromEvent,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  SelectedShift,
  ShiftDrawerService,
} from '../../../services/shift-drawer.service';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';

@Component({
  selector: 'wilson-timeline-drawer',
  templateUrl: './timeline-drawer.component.html',
  styleUrls: ['./timeline-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ShiftQuickCheckService, DrawerGroupingActionsService],
  standalone: false,
})
export class TimelineDrawerComponent implements OnInit, OnDestroy {
  readonly closeDrawer = output<void>();

  static counter = 0;
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly shiftsService = inject(ShiftsService);
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly customNzDrawerService = inject(CustomNzDrawerService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );
  private readonly timelineUnassignedShiftValidationsStateService = inject(
    TimelineUnassignedShiftValidationsStateService,
  );
  private readonly translate = inject(TranslateService);
  private readonly message = inject(NzMessageService);
  protected readonly shiftDrawerCloseHelperService = inject(
    ShiftDrawerCloseHelperService,
  );
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  readonly shiftQuickCheckService = inject(ShiftQuickCheckService);
  readonly shiftSeriesService = inject(ShiftSeriesService);
  readonly shiftDrawerService = inject(ShiftDrawerService);
  private readonly drawerGroupingActions = inject(DrawerGroupingActionsService);
  private readonly timelinePresenceService = inject(TimelinePresenceService);
  private readonly stopVisibilityListener$ = new Subject<void>();
  private presenceShiftSubscription?: Subscription;
  private selectedShiftFetchGenerationCount = 0;

  @WilsonState<boolean>(false)
  private isLoadingSelectedShift!: boolean;
  public isLoadingSelectedShift$!: Observable<boolean>;

  @Output()
  deletionConfirmed = new EventEmitter<Shift & { id: string }>();

  @Output()
  updateShiftOutput = new EventEmitter<{
    selectedShift: SelectedShift;
    updatedFormValue: Partial<TimelineShiftDrawerFormValue>;
    updatePublicationStatus: boolean;
  }>();

  @Output()
  reloadedUnassignedShift = new EventEmitter<SelectedShift>();

  private readonly materializedSelectedShiftSignal = toSignal(
    this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$.pipe(
      switchMap((shiftIdWithPosition) => {
        if (!shiftIdWithPosition) {
          this.isLoadingSelectedShift = false;
          return of(null);
        }

        // We only clear loading when this fetch is still the latest selection; superseded streams still run finalize on unsubscribe/completion.
        const requestCounter = ++this.selectedShiftFetchGenerationCount;

        this.isLoadingSelectedShift = true;

        return this.timelineCommonOperationsService
          .fetchShiftWithTranslatedActivityCategoryAndSortActivities(
            shiftIdWithPosition.id,
          )
          .pipe(
            finalize(() => {
              if (requestCounter === this.selectedShiftFetchGenerationCount) {
                this.isLoadingSelectedShift = false;
              }
            }),
          );
      }),
      map((shift) => (shift ?? null) as SelectedShift | null),
    ),
  );
  protected readonly selectedShiftLinkedSignal = linkedSignal<
    SelectedShift | null | undefined
  >(() => this.materializedSelectedShiftSignal());

  refetchShiftTrigger$ = new BehaviorSubject<Date>(new Date());

  ShiftState = ShiftState;
  readonly isSuccessfullySavedSignal = signal<boolean>(false);
  readonly updatedAtSignal = linkedSignal(() => {
    const selectedShift = this.selectedShiftLinkedSignal();
    if (!selectedShift) {
      return '';
    } else {
      return selectedShift.updatedAt;
    }
  });
  readonly isFormInvalid = signal(false);
  readonly isShiftUpdateOperationOngoingSignal = signal<boolean>(false);
  resetFormNotifier$ = new Subject<Date>();

  private clickOutsideOfIgnoredTargetsHandlerRef!: (event: Event) => void;
  FeatureName = FeatureName;
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;

  readonly hasServiceActivitySignal = computed(() => {
    const selectedShift = this.selectedShiftLinkedSignal();
    if (!selectedShift) {
      return false;
    } else {
      return selectedShift.activities.some((a) => a.serviceId);
    }
  });
  protected PublicationStatus = PublicationStatus;
  protected ShiftDrawerCollapseSectionEnum = ShiftDrawerCollapseSectionEnum;
  protected shiftDrawerCollapseSettings$!: Observable<
    Record<ShiftDrawerCollapseSectionEnum, boolean>
  >;

  can = inject(AbilityServiceSignal).can;

  protected drawerId = NzDrawerId.ShiftDrawer;

  readonly shiftViewers = toSignal(
    this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$.pipe(
      switchMap((shiftIdWithPosition) => {
        const shiftId = shiftIdWithPosition?.id;
        if (!shiftId) {
          return of([]);
        }

        return this.timelinePresenceService.getShiftViewers(shiftId);
      }),
    ),
    { initialValue: [] },
  );

  constructor() {
    this.handleDrawerOpened();
  }

  async ngOnInit(): Promise<void> {
    this.drawerGroupingActions.configure({
      onSuccess: async () => {
        const shiftId = this.selectedShiftLinkedSignal()?.id;
        if (shiftId) {
          await this.reloadShiftAndUpdateTimeline(shiftId);
        }
      },
    });

    await firstValueFrom(
      this.timelineCommonOperationsService.setShiftDrawerCollapseSettings(),
    );
    this.getCollapseSettings();

    this.presenceShiftSubscription =
      this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$
        .pipe(
          map((shiftIdWithPosition) => shiftIdWithPosition?.id ?? null),
          distinctUntilChanged(),
        )
        .subscribe((shiftId) => {
          if (shiftId) {
            this.timelinePresenceService.openShift(shiftId);
            return;
          }

          this.timelinePresenceService.closeActiveShift();
        });
  }

  setIsFormPristine(isPristine: boolean): void {
    this.shiftDrawerCloseHelperService.isFormPristine = isPristine;
  }

  public async updateShiftDetails(
    actualShift: SelectedShift,
    {
      updatedFormValue,
      updatePublicationStatus,
    }: {
      updatedFormValue: Partial<TimelineShiftDrawerFormValue>;
      updatePublicationStatus: boolean;
    },
  ): Promise<void> {
    let publicationStatus: PublicationStatus;
    if (updatePublicationStatus) {
      publicationStatus = determinePublicationStatus(
        actualShift.activities,
        actualShift.publicationStatus,
      );
    } else {
      publicationStatus = actualShift.publicationStatus;
    }

    const shiftToUpdate = {
      id: actualShift.id,
      name: (updatedFormValue?.name as string).trim(),
      organizationalUnitId: updatedFormValue?.organizationalUnitId as string,
      shiftCategoryId: updatedFormValue?.shiftCategoryId as string,
      comment: updatedFormValue?.comment as string,
      planningComment: updatedFormValue?.planningComment as string,
      publicationStatus,
      labels: updatedFormValue?.labels || [],
      projectId: updatedFormValue?.projectId || null,
      sharePlanNumber: updatedFormValue?.sharePlanNumber || null,
      maintenanceNumber: updatedFormValue?.maintenanceNumber || null,
    };

    this.isShiftUpdateOperationOngoingSignal.set(true);
    this.isSuccessfullySavedSignal.set(false);
    try {
      await firstValueFrom(this.shiftsService.updateShift(shiftToUpdate));
      await this.reloadShiftAndUpdateTimeline(actualShift.id as string);
      this.resetFormNotifier$.next(new Date());
      this.isSuccessfullySavedSignal.set(true);
    } catch (error) {
      captureException(error);
      console.error(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      return;
    } finally {
      this.isShiftUpdateOperationOngoingSignal.set(false);
    }
  }

  async reloadShiftAndUpdateTimeline(
    shiftId: string,
    reloadUserStays = false,
  ): Promise<SelectedShift | null> {
    const reloadShiftValidations = true;
    const selectedShift = (await firstValueFrom(
      this.timelineCommonOperationsService.fetchShiftWithTranslatedActivityCategoryAndSortActivities(
        shiftId,
      ),
    )) as SelectedShift;
    if (selectedShift) {
      await this.updateTimelineFromDrawer(
        selectedShift,
        reloadShiftValidations,
      );
      if (selectedShift.userId) {
        await firstValueFrom(
          this.timelineCommonOperationsService.updateUsersAssignmentsState([
            selectedShift.userId,
          ]),
        );
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          selectedShift.userId,
        ]);
        if (reloadUserStays) {
          this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
            [selectedShift.userId],
          );
        }
      }
    }

    return selectedShift;
  }

  protected async updateShiftPublicationStatusAndReloadDrawerAndTimeline(
    actualShift: SelectedShift,
  ): Promise<void> {
    try {
      await this.shiftDrawerService.updateShiftPublicationStatus(
        actualShift.id as string,
      );
      const selectedShift = await this.reloadShiftAndUpdateTimeline(
        actualShift.id as string,
      );

      // Activities are only visible in regular view mode
      const areActivitiesVisible = this.store.selectSnapshot(
        ShiftTimelineSettingsState.areActivitiesVisible,
      );

      if (areActivitiesVisible && selectedShift?.activities.length) {
        const activities = selectedShift.activities.filter(
          (activity) =>
            activity.operationalStatus !== OperationStatus.SkippedByUser,
        );

        const isUnassignedShift = await firstValueFrom(
          this.shiftDrawerService.isUnassignedShift$,
        );

        if (isUnassignedShift) {
          activities.forEach((activity) =>
            this.store.dispatch([
              new TimelineUpsertActivityToUnassignedRegion(
                activity as ActivityWithCategoryAndLocationsAndReportedDetails,
                activity.shiftId as string,
              ),
            ]),
          );
        } else {
          this.store.dispatch([
            new TimelineUpsertActivityV2(
              activities as ActivityWithCategoryAndLocationsAndReportedDetails[],
              selectedShift.id as string,
            ),
          ]);
        }
      }
    } catch (error) {
      console.error(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  async openDriverSearch(): Promise<void> {
    const selectedShiftIdWithPosition = await firstValueFrom(
      this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$,
    );
    if (selectedShiftIdWithPosition) {
      const selectedShiftId = selectedShiftIdWithPosition.id;
      const redirectUrl = this.router.createUrlTree(
        ['shifts', selectedShiftId],
        {
          fragment: OPEN_FIND_STAFF,
        },
      );
      window.open(redirectUrl.toString());
    }
  }

  private async updateTimelineFromDrawer(
    shiftToUpdate: SelectedShift,
    refreshShiftValidations: boolean,
  ): Promise<void> {
    this.selectedShiftLinkedSignal.set(shiftToUpdate as SelectedShift);
    const isUnassignedShift = await firstValueFrom(
      this.shiftDrawerService.isUnassignedShift$,
    );

    if (isUnassignedShift) {
      //Note: This is essential that it is handled by the parent drawer wrapper as we have 2 seperate unassigned region workflows for the cellview mode and standard view mode
      this.reloadedUnassignedShift.emit(shiftToUpdate);
    }

    if (refreshShiftValidations) {
      const validationService = isUnassignedShift
        ? this.timelineUnassignedShiftValidationsStateService
        : this.timelineAssignedShiftValidationsStateService;

      validationService.queueValidationRequest([shiftToUpdate.id], {
        forceRefresh: true,
      });

      await this.shiftQuickCheckService.validateShiftForQuickCheck(
        shiftToUpdate.id as string,
        shiftToUpdate.userId as string,
      );
    }
  }

  async updateShiftNecessaryStay(value: boolean): Promise<void> {
    const selectedShift = this.selectedShiftLinkedSignal();
    if (selectedShift) {
      const updatedShift = { ...selectedShift, ignoreStayNecessary: value };

      await firstValueFrom(this.shiftsService.updateShift(updatedShift));
      await this.reloadShiftAndUpdateTimeline(updatedShift.id as string);
      if (selectedShift.userId && !value) {
        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          selectedShift.userId,
        ]);
      }
    }
  }

  deleteActivity(activity: Activity): void {
    this.store.dispatch(
      new TimelineRemoveActivityFromShiftV2(
        activity.id as string,
        activity.shiftId as string,
      ),
    );
  }

  getCollapseSettings(): void {
    this.shiftDrawerCollapseSettings$ = this.store
      .select(AuthState.userId)
      .pipe(
        filter((userId) => !!userId),
        switchMap((userId) =>
          this.store.select(
            ShiftDrawerSettingsState.getShiftDrawerCollapseSettings(
              userId as string,
            ),
          ),
        ),
      );
  }

  onCollapseChange(
    isCollapsed: boolean,
    section: ShiftDrawerCollapseSectionEnum,
  ): void {
    this.store.dispatch(
      new SetShiftDrawerCollapseSectionSettings(
        this.store.selectSnapshot(AuthState.userId) as string,
        section,
        isCollapsed,
      ),
    );
  }

  private handleDrawerOpened(): void {
    this.customNzDrawerService.removeClickHandler(
      this.clickOutsideOfIgnoredTargetsHandlerRef,
    );
    this.customNzDrawerService.showMask();
    this.clickOutsideOfIgnoredTargetsHandlerRef =
      this.customNzDrawerService.registerClickOutsideOfIgnoredTargetsHandler(
        () => {
          this.shiftDrawerCloseHelperService.checkPristineAndCloseDrawer({
            onOkCallback: this.closeDrawerConfirmCallback.bind(this),
          });
        },
      );
    this.startVisibilityListener();
  }

  private handleDrawerClosed(): void {
    this.customNzDrawerService.removeClickHandler(
      this.clickOutsideOfIgnoredTargetsHandlerRef,
    );
    this.customNzDrawerService.hideMask();
    this.shiftDrawerCloseHelperService.isFormPristine = true;
    this.stopVisibilityListener();
  }

  private startVisibilityListener(): void {
    this.stopVisibilityListener();

    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        debounceTime(300),
        switchMap(
          () =>
            this.shiftMultiSelectHelperService
              .singleSelectedShiftIdWithPosition$,
        ),
        filter((shift) => !!shift),
        takeUntil(this.stopVisibilityListener$),
      )
      .subscribe((shift) => {
        this.reloadShiftAndUpdateTimeline(shift.id);
      });
  }

  private stopVisibilityListener(): void {
    if (!this.stopVisibilityListener$.closed) {
      this.stopVisibilityListener$.next();
    }
  }

  private closeDrawerConfirmCallback(): void {
    this.shiftMultiSelectHelperService.clearSelection();
    this.store.dispatch([new SetDemarcatorRange([])]);
  }

  toggleChangeHistoryDrawer(): void {
    if (this.store.selectSnapshot(AuditTrailDrawerState.isOpen)) {
      this.store.dispatch(new SetAuditTrailDrawerOpenState(false));
    } else {
      this.store.dispatch(
        new SetAuditTrailDrawerCurrentShiftIdState(
          this.selectedShiftLinkedSignal()?.id as string,
        ),
      );
      this.store.dispatch(new SetAuditTrailDrawerOpenState(true));
    }
  }

  ngOnDestroy(): void {
    this.cleanupRealtimeVisibility();
    this.handleDrawerClosed();
    if (!this.stopVisibilityListener$.closed) {
      this.stopVisibilityListener$.complete();
    }
  }

  private cleanupRealtimeVisibility(): void {
    this.presenceShiftSubscription?.unsubscribe();
    this.timelinePresenceService.closeActiveShift();
  }
}
